import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { getApiBase } from './apiBase';

function apiFetch(path, options = {}) {
  const url = `${getApiBase()}${path}`;
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
}

export default function Connections({
  connectionsList: connectionsListProp,
  onRefreshConnections,
  selectedConnectionId: selectedConnectionIdProp,
  onSelectConnectionId,
  openNewConnectionModal,
  onOpenNewConnectionHandled,
}) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    hcm_url: '',
    username: '',
    password: '',
    oracle_username: '',
    oracle_password: '',
  });
  const [selectedIdLocal, setSelectedIdLocal] = useState(null);
  const [modalError, setModalError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testingConnectionId, setTestingConnectionId] = useState(null);
  /** Shown after Test succeeds (Oracle connectivity OK). */
  const [testOk, setTestOk] = useState(null);

  const useParentList = connectionsListProp != null;
  const connectionsDisplay = useParentList ? connectionsListProp : connections;
  const selectedId = selectedConnectionIdProp !== undefined && selectedConnectionIdProp !== null
    ? selectedConnectionIdProp
    : selectedIdLocal;

  const setSelectedId = (id) => {
    if (onSelectConnectionId) onSelectConnectionId(id);
    else setSelectedIdLocal(id);
  };

  const refresh = useCallback(async () => {
    if (useParentList) {
      if (onRefreshConnections) onRefreshConnections();
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/connections');
      if (!res.ok) {
        if (res.status === 401) throw new Error('Not logged in');
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `Request failed (${res.status})`);
      }
      const data = await res.json();
      const list = Array.isArray(data.connections) ? data.connections : [];
      setConnections(list);
    } catch (err) {
      setError(err.message);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  }, [useParentList, onRefreshConnections]);

  useEffect(() => {
    if (!useParentList) refresh();
  }, [useParentList, refresh]);

  useEffect(() => {
    if (openNewConnectionModal && onOpenNewConnectionHandled) {
      setEditingId(null);
      setFormData({ name: '', hcm_url: '', username: '', password: '', oracle_username: '', oracle_password: '' });
      setModalError(null);
      setShowModal(true);
      onOpenNewConnectionHandled();
    }
  }, [openNewConnectionModal, onOpenNewConnectionHandled]);

  const openNew = () => {
    setEditingId(null);
    setFormData({ name: '', hcm_url: '', username: '', password: '', oracle_username: '', oracle_password: '' });
    setModalError(null);
    setShowModal(true);
  };

  const openEdit = (conn) => {
    setEditingId(conn.id);
    setFormData({
      name: conn.connection_name || '',
      hcm_url: conn.application_hcm_url || '',
      username: conn.username || '',
      password: '', // never send stored password to client; "leave blank to keep"
      oracle_username: conn.oracle_username || '',
      oracle_password: '',
    });
    setModalError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setModalError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name?.trim() || !formData.hcm_url?.trim()) return;
    setSaving(true);
    setError(null);
    setModalError(null);
    try {
      const payload = {
        connection_name: formData.name.trim(),
        application_hcm_url: formData.hcm_url.trim(),
        username: formData.username?.trim() ?? '',
        oracle_username: formData.oracle_username?.trim() ?? '',
      };
      if (formData.oracle_password) {
        payload.oracle_password = formData.oracle_password;
      }
      if (editingId) {
        if (formData.password !== undefined && formData.password !== '') {
          payload.password = formData.password;
        }
        const res = await apiFetch(`/api/connections/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Failed (${res.status})`);
        }
      } else {
        payload.password = formData.password ?? '';
        const res = await apiFetch('/api/connections', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Failed (${res.status})`);
        }
      }
      await refresh();
      if (onRefreshConnections) onRefreshConnections();
      closeModal();
    } catch (err) {
      setModalError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete connection "${name}"?`)) return;
    try {
      const res = await apiFetch(`/api/connections/${id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Delete failed');
      }
      await refresh();
      if (onRefreshConnections) onRefreshConnections();
      if (selectedId === id) setSelectedId(null);
      if (testOk?.id === id) setTestOk(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTestConnection = async (conn) => {
    setTestingConnectionId(conn.id);
    setError(null);
    setTestOk(null);
    try {
      const res = await apiFetch(`/api/connections/${conn.id}/test`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || `Test failed (${res.status})`);
      }
      if (!data.connectivity?.ok) {
        setTestOk(null);
        setError(data.connectivity?.message || 'Connection test failed');
        return;
      }
      setError(null);
      setTestOk({
        id: conn.id,
        name: conn.connection_name || 'Unnamed',
      });
    } catch (err) {
      setTestOk(null);
      setError(err.message);
    } finally {
      setTestingConnectionId(null);
    }
  };

  const selectedConnection = connectionsDisplay.find((c) => c.id === selectedId);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-auto p-6">
      <div className="max-w-3xl w-full">
        {/* Connections section: header with + and all content under it */}
        <div className="card-panel overflow-hidden">
          <div className="flex items-center justify-between gap-4 border-b border-slate-200 bg-slate-50/80 px-4 py-3">
            <h2 className="text-lg font-semibold text-slate-900">Connections</h2>
            <button
              type="button"
              onClick={openNew}
              className="inline-flex items-center gap-1.5 rounded-lg bg-sky-100 px-3 py-2 text-sm font-medium text-blue-900 shadow-sm ring-1 ring-sky-300 hover:bg-sky-200"
              title="Add connection"
            >
              <Plus className="h-5 w-5" />
              New connection
            </button>
          </div>

          <div className="p-4 space-y-4">
            {error && (
              <p className="text-sm text-red-600">{error}</p>
            )}

            {testOk && (
              <div
                className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
                role="status"
              >
                <span className="font-medium">Testing connection to </span>
                <span className="font-semibold">&quot;{testOk.name}&quot;</span>
                <span className="font-medium">: OK</span>
              </div>
            )}

            {loading && !useParentList ? (
              <p className="text-slate-500 text-sm">Loading…</p>
            ) : connectionsDisplay.length === 0 && !showModal ? (
              <div className="py-8 text-center text-slate-500 text-sm rounded-lg border border-dashed border-slate-200">
                No connections yet. Click &quot;New connection&quot; above to add one.
              </div>
            ) : (
              <div className="space-y-2">
                {connectionsDisplay.map((conn) => (
                  <div
                    key={conn.id}
                    className={`flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ${
                      selectedId === conn.id
                        ? 'border-slate-400 bg-slate-50 ring-1 ring-slate-400'
                        : 'border-slate-200 bg-white hover:bg-slate-50/50'
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setSelectedId(conn.id)}
                      className="flex-1 min-w-0 text-left"
                    >
                      <span className="font-medium text-slate-900 block truncate">
                        {conn.connection_name || 'Unnamed'}
                      </span>
                      <span className="text-xs text-slate-500 truncate block">
                        {conn.application_hcm_url || '—'}
                      </span>
                      {conn.username && (
                        <span className="text-xs text-slate-500 truncate block">
                          User: {conn.username}
                        </span>
                      )}
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => handleTestConnection(conn)}
                        disabled={testingConnectionId === conn.id}
                        className="rounded-lg border border-teal-300 bg-teal-50/80 px-2 py-1 text-xs font-medium text-teal-800 hover:bg-teal-100 disabled:opacity-50"
                        title="Test connection"
                      >
                        {testingConnectionId === conn.id ? 'Testing…' : 'Test'}
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(conn)}
                        className="rounded-lg p-2 text-sky-600 hover:bg-sky-50 hover:text-sky-800"
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(conn.id, conn.connection_name)}
                        className="rounded-lg p-2 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {selectedConnection && (
              <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 mt-4">
                <h3 className="text-base font-semibold text-slate-900 mb-3">
                  {selectedConnection.connection_name || 'Connection'}
                </h3>
                <dl className="space-y-2 text-sm">
                  <div>
                    <dt className="text-slate-500 font-medium">Application HCM URL</dt>
                    <dd className="text-slate-900 break-all">{selectedConnection.application_hcm_url || '—'}</dd>
                  </div>
                  {selectedConnection.username && (
                    <div>
                      <dt className="text-slate-500 font-medium">Username</dt>
                      <dd className="text-slate-900">{selectedConnection.username}</dd>
                    </div>
                  )}
                  {(selectedConnection.oracle_username || selectedConnection.oracle_rest_configured) && (
                    <div>
                      <dt className="text-slate-500 font-medium">Oracle REST user</dt>
                      <dd className="text-slate-900">{selectedConnection.oracle_username || '(password only)'}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-[90%] p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingId ? 'Edit connection' : 'New connection'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="conn-name" className="block text-sm font-medium text-slate-700 mb-1">
                  Connection name
                </label>
                <input
                  id="conn-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g. Production HCM"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                  required
                />
              </div>
              <div>
                <label htmlFor="conn-url" className="block text-sm font-medium text-slate-700 mb-1">
                  Application HCM URL
                </label>
                <input
                  id="conn-url"
                  type="url"
                  value={formData.hcm_url}
                  onChange={(e) => setFormData((p) => ({ ...p, hcm_url: e.target.value }))}
                  placeholder="https://your-instance.fa.us2.oraclecloud.com"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                  required
                />
              </div>
              <div>
                <label htmlFor="conn-username" className="block text-sm font-medium text-slate-700 mb-1">
                  Username
                </label>
                <input
                  id="conn-username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData((p) => ({ ...p, username: e.target.value }))}
                  placeholder="Oracle HCM username"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                  autoComplete="username"
                />
              </div>
              <div>
                <label htmlFor="conn-password" className="block text-sm font-medium text-slate-700 mb-1">
                  Password
                </label>
                <input
                  id="conn-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData((p) => ({ ...p, password: e.target.value }))}
                  placeholder={editingId ? 'Leave blank to keep current' : 'Oracle HCM password'}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                  autoComplete={editingId ? 'new-password' : 'current-password'}
                />
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-3 space-y-3">
                <p className="text-xs text-slate-600">
                  Optional: use a dedicated <strong>Oracle REST / integration</strong> user for HCM REST (time cards,
                  workers, uploads). If set, these calls use Oracle REST user + Oracle REST password, falling back to the
                  main password when Oracle REST password is left blank.
                </p>
                <div>
                  <label htmlFor="conn-oracle-user" className="block text-sm font-medium text-slate-700 mb-1">
                    Oracle REST username
                  </label>
                  <input
                    id="conn-oracle-user"
                    type="text"
                    value={formData.oracle_username}
                    onChange={(e) => setFormData((p) => ({ ...p, oracle_username: e.target.value }))}
                    placeholder="e.g. integration user for REST"
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                    autoComplete="off"
                  />
                </div>
                <div>
                  <label htmlFor="conn-oracle-pass" className="block text-sm font-medium text-slate-700 mb-1">
                    Oracle REST password
                  </label>
                  <input
                    id="conn-oracle-pass"
                    type="password"
                    value={formData.oracle_password}
                    onChange={(e) => setFormData((p) => ({ ...p, oracle_password: e.target.value }))}
                    placeholder={editingId ? 'Leave blank to keep current' : 'Optional if same as main password'}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:ring-2 focus:ring-slate-400 focus:border-slate-400"
                    autoComplete="new-password"
                  />
                </div>
              </div>
              {modalError && <p className="text-sm text-red-600">{modalError}</p>}
              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 rounded-lg bg-sky-100 px-4 py-2 text-sm font-medium text-blue-900 shadow-sm ring-1 ring-sky-300 hover:bg-sky-200 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editingId ? 'Update' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
